import { useToken } from "@gluestack-style/react";
import { Accordion, AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger, Box, Button, HStack, Icon, Text, Typography } from "components/ui";
import { Formik } from "formik";
import { ChevronDown, ChevronRight, Plus } from "lucide-react-native";
import React, { useState, useEffect } from "react";
import { ActivityIndicator } from "react-native";
import { Site, usePostGroupByIdCreatesiteMutation, usePutSitesByIdMutation } from "store/abcoApi";
import * as Yup from "yup";
import FormField from "./FormField";
import AddressPredictions from "./AddressPredictions";
import { router } from "expo-router";
import { useSiteRegion } from "utils/hooks/useSiteSection";

// Google Places API key
const GOOGLE_MAPS_API_KEY = "AIzaSyBCy5ldKKtsCo27OxivXGbT0zh8kfVEE7E";

// Types for Google Places API
interface Prediction {
	place_id: string;
	description: string;
}

interface PlaceDetails {
	address_components: Array<{
		long_name: string;
		short_name: string;
		types: string[];
	}>;
	geometry?: {
		location: {
			lat: number;
			lng: number;
		};
	};
}

interface SiteFormProps {
	site: Site | null;
	parentId: number;
}

export default function SiteForm({ site, parentId }: SiteFormProps) {
	const [createSite] = usePostGroupByIdCreatesiteMutation();
	const [updateSite] = usePutSitesByIdMutation();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [predictions, setPredictions] = useState<Prediction[]>([]);
	const [showPredictions, setShowPredictions] = useState(false);
	const [visibleAddressLines, setVisibleAddressLines] = useState(() => {
		// Show additional lines if they have values, otherwise show minimum of 2 lines
		const hasLine3 = site?.addressLine3 && site.addressLine3.trim() !== "";
		const hasLine4 = site?.addressLine4 && site.addressLine4.trim() !== "";
		const hasLine5 = site?.addressLine5 && site.addressLine5.trim() !== "";

		if (hasLine5) return 5;
		if (hasLine4) return 4;
		if (hasLine3) return 3;
		return 2;
	});
	const region = useSiteRegion();
	const resolvedLightTealXlight = useToken("colors", "secondary");
	const validationSchema = Yup.object().shape({
		name: Yup.string().required("Site name is required").min(3, "Site name must be at least 3 characters"),
		suburb: Yup.string().required("Suburb is required").min(3, "Suburb must be at least 3 characters"),
		state: Yup.string()
			.required("State is required")
			// .test("valid-state", "Please select a valid state", function (value) {
			// 	if (!value) return false;

			// 	if (region === "au") {
			// 		return ["WA", "NSW", "TAS", "QLD", "VIC", "SA", "ACT", "NT"].includes(value);
			// 	} else if (region === "nz") {
			// 		return ["AKL", "BOP", "CAN", "GIS", "HKB", "MWT", "MBH", "NSN", "NTL", "OTA", "STL", "TKI", "TAS", "WKO", "WGN", "WTC"].includes(value);
			// 	}

			// 	return false;
			// })
			.min(2, "State must be at least 2 characters"),
		postcode: Yup.string().required("Postcode is required").min(3, "Postcode must be at least 3 characters"),
		addressLine1: Yup.string(),
		addressLine2: Yup.string(),
		addressLine3: Yup.string(),
		addressLine4: Yup.string(),
		addressLine5: Yup.string(),
		// latitude: Yup.number().nullable(),
		// longitude: Yup.number().nullable(),
		reference1: Yup.string(),
		reference2: Yup.string(),
		siteContactName: Yup.string(),
		siteContactPhone: Yup.string(),
		deliveryInstructions: Yup.string(),
	});

	// Load Google Maps script
	useEffect(() => {
		const loadGoogleMapsScript = () => {
			if (typeof window === "undefined") return;
			if (window.google && window.google.maps) return;

			const script = document.createElement("script");
			script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
			script.async = true;
			script.defer = true;
			document.head.appendChild(script);
		};

		loadGoogleMapsScript();
	}, []);

	// Function to get place predictions
	const getPlacePredictions = async (input: string): Promise<Prediction[]> => {
		if (!input) return [];

		try {
			// Check if Google Maps API is loaded
			if (!window.google || !window.google.maps || !window.google.maps.places) {
				console.error("Google Maps API not loaded");
				return [];
			}

			const service = new window.google.maps.places.AutocompleteService();
			const sessionToken = new window.google.maps.places.AutocompleteSessionToken();

			return new Promise((resolve) => {
				service.getPlacePredictions(
					{
						input,
						componentRestrictions: { country: region === "au" ? "au" : "nz" },
						sessionToken,
					},
					(predictions) => {
						resolve(predictions || []);
					},
				);
			});
		} catch (error) {
			console.error("Error fetching predictions:", error);
			return [];
		}
	};

	// Function to get place details
	const getPlaceDetails = async (placeId: string): Promise<PlaceDetails | null> => {
		try {
			// Check if Google Maps API is loaded
			if (!window.google || !window.google.maps || !window.google.maps.places) {
				console.error("Google Maps API not loaded");
				return null;
			}

			const service = new window.google.maps.places.PlacesService(document.createElement("div"));
			const sessionToken = new window.google.maps.places.AutocompleteSessionToken();

			return new Promise((resolve) => {
				service.getDetails(
					{
						placeId,
						sessionToken,
						fields: ["address_components", "geometry"],
					},
					(place, status) => {
						if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
							// Ensure address_components exists and has the correct type
							const addressComponents = place.address_components || [];

							// Safely handle geometry location
							const location = place.geometry?.location;
							const geometry = location
								? {
										location: {
											lat: location.lat(),
											lng: location.lng(),
										},
									}
								: undefined;

							const details: PlaceDetails = {
								address_components: addressComponents.map((component) => ({
									long_name: component.long_name || "",
									short_name: component.short_name || "",
									types: component.types || [],
								})),
								geometry,
							};
							resolve(details);
						} else {
							resolve(null);
						}
					},
				);
			});
		} catch (error) {
			console.error("Error fetching place details:", error);
			return null;
		}
	};

	// Handle address search
	const handleAddressSearch = async (searchText: string) => {
		if (!searchText) {
			setPredictions([]);
			return;
		}

		const predictions = await getPlacePredictions(searchText);
		setPredictions(predictions);
	};

	// Handle prediction selection
	const handlePredictionSelect = async (prediction: Prediction, formik: any) => {
		const place = await getPlaceDetails(prediction.place_id);
		if (!place) return;

		const addressComponents: Record<string, string> = {};
		place.address_components.forEach((component) => {
			const type = component.types[0];
			switch (type) {
				case "street_number":
					addressComponents.number = component.long_name;
					break;
				case "route":
					addressComponents.street = component.long_name;
					break;
				case "locality":
					formik.setFieldValue("suburb", component.long_name);
					break;
				case "administrative_area_level_1":
					formik.setFieldValue("state", component.short_name);
					break;
				case "postal_code":
					formik.setFieldValue("postcode", component.long_name);
					break;
			}
		});

		// Set the street address
		if (addressComponents.number && addressComponents.street) {
			formik.setFieldValue("addressLine1", `${addressComponents.number} ${addressComponents.street}`);
		} else if (addressComponents.street) {
			formik.setFieldValue("addressLine1", addressComponents.street);
		}

		// Set latitude and longitude if available
		if (place.geometry?.location) {
			formik.setFieldValue("latitude", place.geometry.location.lat);
			formik.setFieldValue("longitude", place.geometry.location.lng);
		}

		setPredictions([]);
		setShowPredictions(false);
	};

	const addAddressLine = () => {
		if (visibleAddressLines < 5) {
			setVisibleAddressLines(visibleAddressLines + 1);
		}
	};

	return (
		<Box>
			<Formik
				initialValues={{
					name: site?.name ?? "",
					suburb: site?.suburb ?? "",
					state: site?.state ?? "",
					postcode: site?.postcode ?? "",
					addressLine1: site?.addressLine1 ?? "",
					addressLine2: site?.addressLine2 ?? "",
					addressLine3: site?.addressLine3 ?? "",
					addressLine4: site?.addressLine4 ?? "",
					addressLine5: site?.addressLine5 ?? "",
					latitude: site?.latitude ?? null,
					longitude: site?.longitude ?? null,
					reference1: site?.reference1 ?? "",
					reference2: site?.reference2 ?? "",
					siteContactName: site?.siteContactName ?? "",
					siteContactPhone: site?.siteContactPhone ?? "",
					deliveryInstructions: site?.deliveryInstructions ?? "",
				}}
				validationSchema={validationSchema}
				onSubmit={async (values) => {
					setIsSubmitting(true);
					try {
						if (site?.id) {
							// Update existing site
							await updateSite({
								id: site.id,
								site: {
									...site,
									...values,
								},
							}).unwrap();
						} else {
							// Create new site
							await createSite({ site: values, id: parentId })
								.unwrap()
								.then((res: any) => {
									return router.push({
										pathname: "admin/groups/[id]",
										params: { id: res },
									});
								});
						}
					} catch (error) {
						console.error("Error saving site:", error);
					} finally {
						setIsSubmitting(false);
					}
				}}
			>
				{(formik) => (
					<Box gap={20}>
						<FormField label="Site Name" name="name" placeholder="Enter site name" formik={formik} />

						<Accordion type="multiple" defaultValue={["basicInfo", "address", "reference", "contact", "additional"]}>
							<AccordionItem value="address" borderColor="$neutral100">
								<AccordionHeader>
									<AccordionTrigger style={{ paddingHorizontal: 0 }}>
										{({ isExpanded }) => (
											<HStack alignItems="center" justifyContent="space-between" width="100%">
												<Typography.H6>Address Details</Typography.H6>
												<Icon as={isExpanded ? ChevronDown : ChevronRight} color="$primarylight" size="lg" />
											</HStack>
										)}
									</AccordionTrigger>
								</AccordionHeader>
								<AccordionContent style={{ paddingHorizontal: 0 }}>
									<Box gap={24}>
										<Box position="relative" zIndex={99}>
											<FormField
												label="Address Line 1"
												name="addressLine1"
												placeholder="Enter address line 1"
												formik={formik}
												onChange={(e: any) => {
													const value = e.target.value;
													formik.handleChange("addressLine1")(value);
													handleAddressSearch(value);
												}}
												onFocus={() => setShowPredictions(true)}
												onBlur={() => {
													setTimeout(() => setShowPredictions(false), 200);
												}}
											/>
											{showPredictions && predictions.length > 0 && <AddressPredictions predictions={predictions} onSelect={(prediction) => handlePredictionSelect(prediction, formik)} />}
										</Box>
										<FormField label="Address Line 2" name="addressLine2" placeholder="Enter address line 2" formik={formik} />

										{visibleAddressLines >= 3 && <FormField label="Address Line 3" name="addressLine3" placeholder="Enter address line 3" formik={formik} />}

										{visibleAddressLines >= 4 && <FormField label="Address Line 4" name="addressLine4" placeholder="Enter address line 4" formik={formik} />}

										{visibleAddressLines >= 5 && <FormField label="Address Line 5" name="addressLine5" placeholder="Enter address line 5" formik={formik} />}

										{visibleAddressLines < 5 && (
											<Button
												variant="outline"
												onPress={addAddressLine}
												sx={{
													alignSelf: "flex-start",
													paddingHorizontal: 12,
													paddingVertical: 8,
													borderColor: "$primarylight",
													borderStyle: "dashed",
												}}
											>
												<HStack alignItems="center" gap={6}>
													<Icon as={Plus} size="sm" color="$primarylight" />
													<Text sx={{ color: "$primarylight", fontSize: 14 }}>Add Address Line</Text>
												</HStack>
											</Button>
										)}
										<FormField label="Suburb" name="suburb" placeholder="Enter suburb" formik={formik} />
										<FormField label="State" name="state" placeholder="Enter state" formik={formik} />
										<FormField label="Postcode" name="postcode" placeholder="Enter postcode" formik={formik} />
										{/* <HStack gap={12}>
											<FormField label="Latitude" name="latitude" placeholder="Enter latitude" formik={formik} type="input" />
											<FormField label="Longitude" name="longitude" placeholder="Enter longitude" formik={formik} type="input" />
										</HStack> */}
									</Box>
								</AccordionContent>
							</AccordionItem>

							<AccordionItem value="reference" borderColor="$neutral100">
								<AccordionHeader>
									<AccordionTrigger style={{ paddingHorizontal: 0 }}>
										{({ isExpanded }) => (
											<HStack alignItems="center" justifyContent="space-between" width="100%">
												<Typography.H6>Reference Information</Typography.H6>
												<Icon as={isExpanded ? ChevronDown : ChevronRight} color="$primarylight" size="lg" />
											</HStack>
										)}
									</AccordionTrigger>
								</AccordionHeader>
								<AccordionContent style={{ paddingHorizontal: 0 }}>
									<Box gap={12}>
										<FormField label="Reference 1" name="reference1" placeholder="Enter reference 1" formik={formik} />
										<FormField label="Reference 2" name="reference2" placeholder="Enter reference 2" formik={formik} />
									</Box>
								</AccordionContent>
							</AccordionItem>
							<AccordionItem value="contact" borderColor="$neutral100">
								<AccordionHeader>
									<AccordionTrigger style={{ paddingHorizontal: 0 }}>
										{({ isExpanded }) => (
											<HStack alignItems="center" justifyContent="space-between" width="100%">
												<Typography.H6>Contact Information</Typography.H6>
												<Icon as={isExpanded ? ChevronDown : ChevronRight} color="$primarylight" size="lg" />
											</HStack>
										)}
									</AccordionTrigger>
								</AccordionHeader>
								<AccordionContent style={{ paddingHorizontal: 0 }}>
									<Box gap={12}>
										<FormField label="Contact Name" name="siteContactName" placeholder="Enter contact name" formik={formik} />
										<FormField label="Contact Phone" name="siteContactPhone" placeholder="Enter contact phone" formik={formik} />
									</Box>
								</AccordionContent>
							</AccordionItem>
							<AccordionItem value="additional" borderColor="$neutral100">
								<AccordionHeader>
									<AccordionTrigger style={{ paddingHorizontal: 0 }}>
										{({ isExpanded }) => (
											<HStack alignItems="center" justifyContent="space-between" width="100%">
												<Typography.H6>Additional Information</Typography.H6>
												<Icon as={isExpanded ? ChevronDown : ChevronRight} color="$primarylight" size="lg" />
											</HStack>
										)}
									</AccordionTrigger>
								</AccordionHeader>
								<AccordionContent style={{ paddingHorizontal: 0 }}>
									<Box gap={12}>
										<FormField
											label="Delivery Instructions"
											name="deliveryInstructions"
											placeholder="Enter delivery instructions"
											formik={formik}
											type="input"
											helperText="Add any special delivery instructions or notes for this site"
										/>
									</Box>
								</AccordionContent>
							</AccordionItem>
						</Accordion>

						<Button width={120} variant="secondary" onPress={() => formik.handleSubmit()} disabled={formik.isSubmitting}>
							<Text>{site?.id ? "Update" : "Save"}</Text>
							{isSubmitting && <ActivityIndicator size="small" style={{ position: "absolute", right: 12 }} />}
						</Button>
					</Box>
				)}
			</Formik>
		</Box>
	);
}
